import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import PromptGrid from '@/components/Prompts/PromptGrid';
import {
  PROMPT_GRID_COMPACT_MAX_COLUMNS,
  PROMPT_GRID_COMPACT_MIN_CARD_WIDTH,
} from '@/components/Prompts/promptGridLayout';
import { promptsApi } from '@/lib/api';

/** Matches `prompts/[slug].jsx` inner column: maxWidth 800 minus horizontal padding. */
const DETAIL_GRID_MAX_WIDTH = 752;

/**
 * @param {object}   props
 * @param {string}   props.slug          source prompt/tool slug
 * @param {Function} [props.loadRelated] async (slug, { limit }) => { prompts: [...] }.
 *                                        Defaults to sibling prompts; pass
 *                                        `toolsApi.relatedPrompts` to surface the
 *                                        recipes related to a tool.
 * @param {string}   [props.title]       header label override
 * @param {boolean}  [props.compact]     denser grid (up to 5 cols on wide screens)
 */
function PromptRelatedStrip({ slug, loadRelated, title, compact = false }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const [prompts, setPrompts] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setPrompts(null);
    (async () => {
      try {
        const loader = loadRelated || ((s, opts) => promptsApi.related(s, opts));
        const data = await loader(slug, { limit: 8 });
        if (cancelled) return;
        setPrompts(Array.isArray(data?.prompts) ? data.prompts : []);
      } catch {
        if (!cancelled) setPrompts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, loadRelated]);

  const handlePromptPress = useCallback((prompt) => {
    if (prompt?.slug) router.push(`/prompts/${prompt.slug}`);
  }, [router]);

  const styles = useMemo(() => StyleSheet.create({
    wrap: { gap: 12 },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    sectionTitle: { marginBottom: 10 },
  }), []);

  if (prompts === null) {
    return (
      <View style={styles.wrap}>
        <ActivityIndicator size="small" color={colors.text_color_secondary} />
      </View>
    );
  }
  if (!prompts.length) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.headerRow}>
        <Text tag="h2" size="xxxl" weight="bold" color="primary" style={styles.sectionTitle}>
          {title || t('pages.prompts.relatedTitle') || 'More from the library'}
        </Text>
      </View>
      <PromptGrid
        prompts={prompts}
        onPromptPress={handlePromptPress}
        contentMaxWidth={DETAIL_GRID_MAX_WIDTH}
        {...(compact ? {
          maxColumns: PROMPT_GRID_COMPACT_MAX_COLUMNS,
          minCardWidth: PROMPT_GRID_COMPACT_MIN_CARD_WIDTH,
        } : {})}
      />
    </View>
  );
}

export default React.memo(PromptRelatedStrip);
